<template>
    <div class="cs-page" v-if="model" :key="model.general.uuid">
        <protokol-floating-settings v-if="$isPlatformEnv('dev')">
            <b-button variant="dark" :to="listLink" :append="true" v-if="$can('see ' + model.general.getRef())">{{ $t('list') }}</b-button>
        </protokol-floating-settings>

        <!-- Header -->
        <div class="cs-header">
            <div class="cs-header-left">
                <router-link to="/admin/components" class="cs-back">
                    <b-icon icon="arrow-left" font-scale="1.1"></b-icon>
                </router-link>
                <div class="cs-identity">
                    <div class="cs-title-row">
                        <span class="cs-dot" :class="model.general.is_active ? 'cs-dot--active' : 'cs-dot--inactive'"></span>
                        <h4 class="cs-title">{{ $translateLabel(model.general.label, model.general.name) }}</h4>
                        <span v-if="settingsChanged" class="cs-unsaved" title="Unsaved changes"></span>
                    </div>
                    <div class="cs-meta">
                        <span v-if="model.general.scope" class="cs-scope-chip">{{ model.general.scope }}</span>
                        <span class="cs-ref">{{ model.general.getRef() }}</span>
                    </div>
                </div>
            </div>
            <div class="cs-header-actions">
                <b-button v-if="settingsChanged" size="sm" variant="outline-secondary" @click="restoreSettings" class="mr-2">
                    <b-icon icon="arrow-counterclockwise" class="mr-1"></b-icon>{{ $t('restoreChanges') }}
                </b-button>
                <b-button v-if="settingsChanged" size="sm" variant="success" @click="saveSettings" :disabled="saving">
                    <b-spinner v-if="saving" small class="mr-1" />
                    <b-icon v-else icon="check2" class="mr-1"></b-icon>
                    {{ $t('saveChanges') }}
                </b-button>
                <b-dropdown size="sm" variant="outline-secondary" right no-caret class="ml-2">
                    <template #button-content>
                        <b-icon icon="three-dots-vertical"></b-icon>
                    </template>
                    <b-dropdown-item v-if="canRemove" @click="clone.modal = true">
                        <b-icon icon="files" class="mr-2"></b-icon>{{ $t('clone') }}
                    </b-dropdown-item>
                    <b-dropdown-item @click="copySchema()">
                        <b-icon icon="clipboard" class="mr-2"></b-icon>{{ $t('copySchema') }}
                    </b-dropdown-item>
                </b-dropdown>
            </div>
        </div>

        <!-- Version Pipeline -->
        <div class="cs-versions">
            <div class="cs-version-slot">
                <span class="cs-version-label">{{ $t('previewVersion') }}</span>
                <b-form-select :options="componentVersions" v-model="currentVersion" @change="previewVersion" size="sm" class="cs-version-select"></b-form-select>
            </div>
            <b-icon icon="arrow-right" class="cs-version-arrow"></b-icon>
            <div class="cs-version-slot">
                <span class="cs-version-label">Dev</span>
                <div class="cs-version-info">
                    <span v-if="model.general.dev_version" class="cs-version-chip cs-version-chip--dev">{{ model.general.dev_version }}</span>
                    <span v-else class="text-muted cs-version-none">&mdash;</span>
                    <b-button size="sm" variant="outline-success" @click="deployVersion('dev', currentVersion)" :disabled="!currentVersion || currentVersion === model.general.dev_version" class="cs-deploy-btn" :title="$t('deploy') + ' to Dev'">
                        <b-icon icon="cloud-upload" font-scale="0.85"></b-icon>
                    </b-button>
                </div>
            </div>
            <b-icon icon="arrow-right" class="cs-version-arrow"></b-icon>
            <div class="cs-version-slot">
                <span class="cs-version-label">Live</span>
                <div class="cs-version-info">
                    <span v-if="model.general.public_version" class="cs-version-chip cs-version-chip--live">{{ model.general.public_version }}</span>
                    <span v-else class="text-muted cs-version-none">&mdash;</span>
                    <b-button size="sm" variant="outline-primary" @click="deployVersion('live', currentVersion)" :disabled="!currentVersion || currentVersion === model.general.public_version" class="cs-deploy-btn" :title="$t('deploy') + ' to Live'">
                        <b-icon icon="cloud-upload" font-scale="0.85"></b-icon>
                    </b-button>
                </div>
            </div>
        </div>

        <!-- Tabs + Content -->
        <b-tabs v-model="tabIndex" class="cs-tabs" content-class="cs-tab-content" no-fade>
            <!-- General -->
            <b-tab @click="tabChange('general')">
                <template #title><b-icon icon="gear" class="mr-1" /> {{ $t('general') }}</template>

                <!-- Identity Card -->
                <div class="cs-card">
                    <h6 class="cs-card-title"><b-icon icon="card-heading" class="mr-2" />Identity</h6>
                    <div class="cs-form-grid">
                        <div class="cs-field">
                            <label>{{ $t('uuid') }}</label>
                            <b-form-input :value="model.general.uuid" disabled size="sm"></b-form-input>
                            <small class="text-muted">{{ $t('uniqueId') }}</small>
                        </div>
                        <div class="cs-field">
                            <label>{{ $t('name') }}</label>
                            <b-form-input :value="model.general.getRef()" disabled size="sm"></b-form-input>
                            <small class="text-muted">{{ $t('registeredComponent') }}</small>
                        </div>
                        <div class="cs-field">
                            <label>Icon</label>
                            <choose-icon :item="model.general" v-model="model.general.icon"/>
                        </div>
                        <div class="cs-field">
                            <label>{{ $t('label') }}</label>
                            <Labeli18n v-model="model.general.label" :disabled="!canRemove" />
                        </div>
                        <div class="cs-field">
                            <label>{{ $t('tag') }}</label>
                            <v-select v-model="model.general.tag" noDrop taggable :disabled="!canRemove"/>
                            <small class="text-muted">{{ $t('tagDescription') }}</small>
                        </div>
                        <div class="cs-field">
                            <label>{{ $t('scope') }}</label>
                            <v-select v-model="model.general.scope" :disabled="!canRemove" noDrop taggable/>
                            <small class="text-muted">{{ $t('scopeDescription') }}</small>
                        </div>
                    </div>
                </div>

                <!-- Configuration Card -->
                <div class="cs-card">
                    <h6 class="cs-card-title"><b-icon icon="sliders" class="mr-2" />Configuration</h6>
                    <div class="cs-form-grid">
                        <div class="cs-field">
                            <label>{{ $t('sort') }}</label>
                            <b-form-input type="number" v-model.number="model.general.sort" size="sm"></b-form-input>
                            <small class="text-muted">{{ $t('sortSidebar') }}</small>
                        </div>
                        <div class="cs-field">
                            <label>{{ $t('workspaces') }}</label>
                            <v-select name="workspace" label="text" v-model="model.workspaces" multiple v-if="workspaces" :options="workspaces" :reduce="w => w.value" />
                            <small class="text-muted">{{ $t('assignComponent') }}</small>
                        </div>
                    </div>
                    <div class="cs-toggles">
                        <div class="cs-toggle-row">
                            <div>
                                <span class="cs-toggle-label">{{ $t('public') }}</span>
                                <small class="cs-toggle-desc">{{ $t('publishComponent') }}</small>
                            </div>
                            <c-switch class="mx-1" color="success" variant="pill" v-model="model.general.is_public" :disabled="!canBePublic"/>
                        </div>
                        <div class="cs-toggle-row">
                            <div>
                                <span class="cs-toggle-label">{{ $t('active') }}</span>
                                <small class="cs-toggle-desc">{{ canBeActive ? '' : "Component can't be active without proper fields setup" }}</small>
                            </div>
                            <c-switch class="mx-1" color="success" variant="pill" v-model="model.general.is_active" :disabled="!canBeActive"/>
                        </div>
                    </div>
                    <div v-if="model.general.dependencies && model.general.dependencies.length > 0" class="cs-dependencies">
                        <label>{{ $t('dependencies') }}</label>
                        <v-select
                            :value="model.general.dependencies"
                            :options="model.general.dependencies"
                            :get-option-label="function(opt) { return opt }"
                            :reduce="function(opt) { return opt }"
                            multiple
                            noDrop
                            :clearable="false"
                            @input="onDependencyChange">
                            <template #selected-option-container="{ option, deselect }">
                                <span class="vs__selected">
                                    {{ option.label || option }}
                                    <button
                                        v-if="(option.label || option) !== 'platform'"
                                        type="button"
                                        title="Remove"
                                        class="vs__deselect"
                                        @click.stop="onDependencyChange((model.general.dependencies || []).filter(function(d) { return d !== (option.label || option) }))"
                                        :aria-label="$t('removeDependency') + ' ' + (option.label || option)">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10"><path d="M6.895455 5l2.842897-2.842898c.348864-.348863.348864-.914488 0-1.263636L9.106534.261648c-.348864-.348864-.914489-.348864-1.263636 0L5 3.104545 2.157102.261648c-.348863-.348864-.914488-.348864-1.263636 0L.261648.893466c-.348864.348864-.348864.914489 0 1.263636L3.104545 5 .261648 7.842898c-.348864.348863-.348864.914488 0 1.263636l.631818.631818c.348864.348864.914489.348864 1.263636 0L5 6.895455l2.842898 2.842897c.348863.348864.914488.348864 1.263636 0l.631818-.631818c.348864-.348864.348864-.914489 0-1.263636L6.895455 5z"/></svg>
                                    </button>
                                </span>
                            </template>
                        </v-select>
                        <small class="text-muted">{{ $t('dependenciesDescription') }}</small>
                    </div>
                </div>

                <!-- Metadata Card -->
                <div class="cs-card">
                    <h6 class="cs-card-title"><b-icon icon="clock-history" class="mr-2" />Metadata</h6>
                    <div class="cs-form-grid">
                        <div class="cs-field">
                            <label>{{ $t('createdAt') }}</label>
                            <span class="cs-readonly-value">{{ $toUserTimeZone(model.general.CreatedAt) }}</span>
                        </div>
                        <div class="cs-field">
                            <label>{{ $t('updatedAt') }}</label>
                            <span class="cs-readonly-value">{{ $toUserTimeZone(model.general.UpdatedAt) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Version Management Card -->
                <div class="cs-card">
                    <h6 class="cs-card-title"><b-icon icon="diagram-2" class="mr-2" />Version Management</h6>
                    <div class="cs-action-grid">
                        <div class="cs-action-item">
                            <div>
                                <span class="cs-action-label">{{ $t('fork') }}</span>
                                <small class="cs-action-desc">{{ $t('createNewVersion') }}</small>
                            </div>
                            <b-button size="sm" variant="outline-secondary" @click="initFork">
                                <b-icon icon="diagram-2" class="mr-1"></b-icon>{{ $t('fork') }}
                            </b-button>
                        </div>
                        <div class="cs-action-item" v-if="canRemove">
                            <div>
                                <span class="cs-action-label">{{ $t('forkFromSchema') }}</span>
                                <small class="cs-action-desc">Create version from JSON schema</small>
                            </div>
                            <b-button size="sm" variant="outline-secondary" @click="forkSchemaModal = true">
                                <b-icon icon="file-earmark-code" class="mr-1"></b-icon>{{ $t('forkFromSchema') }}
                            </b-button>
                        </div>
                        <div class="cs-action-item">
                            <div>
                                <span class="cs-action-label">{{ $t('deploy') }}</span>
                                <small class="cs-action-desc">{{ $t('deploySettings') }}</small>
                            </div>
                            <b-button size="sm" variant="outline-success" @click="deployVersionModal = true">
                                <b-icon icon="cloud-upload" class="mr-1"></b-icon>{{ $t('deploy') }}
                            </b-button>
                        </div>
                    </div>
                </div>

                <!-- Danger Zone -->
                <div class="cs-card cs-card--danger">
                    <h6 class="cs-card-title"><b-icon icon="exclamation-triangle" class="mr-2" />Danger Zone</h6>
                    <div class="cs-action-grid">
                        <div class="cs-action-item">
                            <div>
                                <span class="cs-action-label">{{ $t('removeVersion') }}</span>
                                <small class="cs-action-desc">Remove version {{ currentVersion }}</small>
                            </div>
                            <b-button size="sm" variant="outline-danger" @click="removeCurrentVersion" :disabled="model.general.public_version == currentVersion || model.general.dev_version == currentVersion">
                                <b-icon icon="trash" class="mr-1"></b-icon>{{ $t('removeVersion') }}
                            </b-button>
                        </div>
                        <div class="cs-action-item">
                            <div>
                                <span class="cs-action-label">{{ $t('remove') }}</span>
                                <small class="cs-action-desc">{{ canRemove ? $t('removeComponent') : $t('cantRemoveComponent') }}</small>
                            </div>
                            <b-button size="sm" variant="outline-danger" @click="removeComponent" :disabled="!canRemove">
                                <b-icon icon="trash" class="mr-1"></b-icon>{{ $t('remove') }}
                            </b-button>
                        </div>
                    </div>
                </div>
            </b-tab>

            <!-- Appearance -->
            <b-tab @click="tabChange('appearance')">
                <template #title><b-icon icon="palette" class="mr-1" /> {{ $t('appearance') }}</template>
                <Appearance v-model="model.settings.appearance" :component="model" />
            </b-tab>

            <!-- List -->
            <b-tab @click="tabChange('list')">
                <template #title><b-icon icon="list-ul" class="mr-1" /> {{ $t('list') }}</template>
                <Presets v-model="model.settings.presets" />
                <ListFields v-model="model.settings.list.fields" :component="model" :config="{custom:model.general.is_custom,dependencyProtected:!canRemove}"/>
            </b-tab>

            <!-- Fields -->
            <b-tab @click="tabChange('fields')">
                <template #title><b-icon icon="pencil-square" class="mr-1" /> {{ $t('fields') }}</template>
                <Fields v-model="model.settings.fields" :component="model" :config="{custom:model.general.is_custom,dependencyProtected:!canRemove,isLiveVersion:!canBeUpdated()}"/>
            </b-tab>

            <!-- Model -->
            <b-tab @click="tabChange('model')">
                <template #title><b-icon icon="diagram-3" class="mr-1" /> {{ $t('model') }}</template>
                <UniqueIndexes v-model="model.settings.model.unique_indexes_combinations" :fields="model.settings.fields" />
                <hr class="my-4" />
                <Policies v-model="policies" :component="model" :componentRef="ref" :componentVersion="currentVersion" @input="updatePolicies" />
            </b-tab>

            <!-- Workflow -->
            <b-tab @click="tabChange('workflow')" lazy>
                <template #title><b-icon icon="share" class="mr-1" /> {{ $t('workflow.title') }}</template>
                <b-alert show variant="info" class="mb-3 d-flex align-items-start">
                    <b-icon icon="info-circle-fill" class="mr-2 mt-1 flex-shrink-0"></b-icon>
                    <small>{{ $t('workflow.sharedFunctionsHint') }}</small>
                </b-alert>
                <b-row class="mb-3" v-if="workflowSources.length > 1">
                    <b-col cols="12" md="4">
                        <b-form-group :label="$t('workflowSource')" label-class="font-weight-bold">
                            <v-select
                                v-model="selectedWorkflowSource"
                                :options="workflowSources"
                                label="label"
                                :reduce="source => source.value"
                                :clearable="false"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
                <Events
                    :key="selectedWorkflowSource"
                    v-model="currentWorkflowEvents"
                    :uuid="model.general.uuid"
                    :schema="model.general.getRef()"
                    :fields="model.settings.fields"
                    :extensionName="selectedExtension"
                    :readonly="selectedExtension !== null"
                />
            </b-tab>

            <!-- Functions -->
            <b-tab @click="tabChange('functions')">
                <template #title><b-icon icon="braces" class="mr-1" /> {{ $t('functions.title') }}</template>
                <Functions v-model="model.settings.functions" :id="ref" :config="model.settings.config" :extensions="model.settings.extensions" :idl="currentSchemaIDL" />
            </b-tab>

            <!-- Config -->
            <b-tab @click="tabChange('config')">
                <template #title><b-icon icon="gear-wide-connected" class="mr-1" /> {{ $t('config.title') }}</template>
                <div class="cs-card">
                    <h6 class="cs-card-title"><b-icon icon="code-square" class="mr-2" />{{ $t('componentConfiguration') }}</h6>
                    <CodeEditor v-model="componentConfig" :withButton="false" />
                </div>
                <div v-if="activeExtensions.length > 0">
                    <h6 class="cs-section-title">{{ $t('extensionsConfigurations') }}</h6>
                    <div v-for="extension in activeExtensions" :key="extension.name" class="cs-card mb-3">
                        <div class="cs-ext-header">
                            <b-icon icon="puzzle" class="mr-2"></b-icon>
                            <strong>{{ extension.name }}</strong>
                            <span class="cs-ext-badge">{{ $t('active') }}</span>
                        </div>
                        <CodeEditor
                            :value="getExtensionConfig(extension)"
                            :withButton="false"
                            :readOnly="true"
                        />
                    </div>
                </div>
            </b-tab>

            <!-- Extensions -->
            <b-tab @click="tabChange('extensions')">
                <template #title><b-icon icon="puzzle" class="mr-1" /> {{ $t('extensions.title') }}</template>
                <Extensions v-model="extensions" :component="model" :componentRef="ref" :componentVersion="currentVersion" @input="updateExtensions" />
            </b-tab>

            <!-- IDL -->
            <b-tab @click="tabChange('idl')">
                <template #title><b-icon icon="file-code" class="mr-1" /> {{ $t('idl.title') }}</template>
                <div class="cs-card">
                    <div class="cs-idl-header">
                        <p class="text-muted mb-0">{{ $t('idl.description') }}</p>
                        <div class="cs-idl-actions">
                            <b-button size="sm" variant="outline-primary" @click="generateIdlFromFields">
                                <b-icon icon="lightning" class="mr-1"></b-icon>{{ $t('idl.autoGenerate') }}
                            </b-button>
                            <a href="https://docs.protokol.io/components/idl/" target="_blank" rel="noopener" class="cs-doc-link">
                                <b-icon icon="book" class="mr-1"></b-icon><small>{{ $t('documentation') }}</small>
                            </a>
                        </div>
                    </div>
                    <b-alert v-if="idlFieldWarnings.length" show variant="warning" class="mb-3">
                        <strong><b-icon icon="exclamation-triangle" class="mr-1"></b-icon>{{ $t('idl.typeConflictTitle') }}</strong>
                        <ul class="mb-0 mt-2">
                            <li v-for="w in idlFieldWarnings" :key="w.key">
                                <code>{{ w.key }}</code> — {{ $t('idl.typeConflict', { idlType: w.idlType, widgetType: w.widgetType, module: w.module, type: w.type }) }}
                            </li>
                        </ul>
                    </b-alert>
                    <CodeEditor lang="application/json" :withButton="false" v-model="idlJson" :height="600" />
                </div>
                <div v-if="activeExtensions.length > 0">
                    <h6 class="cs-section-title">{{ $t('extensionsIdls') }}</h6>
                    <div v-for="extension in activeExtensions" :key="extension.name" class="cs-card mb-3">
                        <div class="cs-ext-header">
                            <b-icon icon="puzzle" class="mr-2"></b-icon>
                            <strong>{{ extension.name }}</strong>
                            <span class="cs-ext-badge">{{ $t('active') }}</span>
                        </div>
                        <CodeEditor
                            :value="getExtensionIdl(extension)"
                            :withButton="false"
                            :readOnly="true"
                        />
                    </div>
                </div>
            </b-tab>

            <RenderEvent
                event="settings"
                v-model="model.settings.integrations"
                :disabled="!canBeUpdated()"
                :currentVersion="this.currentVersion"
                :settings="model"
            />
        </b-tabs>

        <!-- Modals -->
        <main-dialog title="Clone component" v-model="clone.modal" @close="clone.modal = false" @ok="cloneComponent" ok-title="Clone">
            <b-form-group label-for="component_name">
                <b-form-input id="component_name" type="text" v-model="clone.name" placeholder="New component name"></b-form-input>
            </b-form-group>
        </main-dialog>

        <main-dialog title="Fork version" v-model="forkVersionModal" @close="forkVersionModal = false" @ok="forkVersion" ok-title="Fork">
            <b-form-group :label="$t('forkFrom')" label-for="version" :label-cols="3" :horizontal="true">
                <b-form-input type="text" v-model="fork.from" disabled></b-form-input>
            </b-form-group>
            <b-form-group :label="$t('version')" label-for="version" :label-cols="3" :horizontal="true">
                <b-form-input type="text" v-model="fork.to"></b-form-input>
            </b-form-group>
        </main-dialog>

        <main-dialog title="Fork from schema" v-model="forkSchemaModal" @close="forkSchemaModal = false" @ok="forkSchemaAction" ok-title="Fork">
            <b-form-group :label="$t('version')" label-for="version" :label-cols="3" :horizontal="true">
                <b-form-input type="text" v-model="forkSchema.version"></b-form-input>
            </b-form-group>
            <b-form-group :label="$t('schema')" label-for="schema" :label-cols="3" :horizontal="true">
                <b-textarea v-model="forkSchema.data" />
            </b-form-group>
        </main-dialog>

        <main-dialog title="Deploy version" v-model="deployVersionModal" @close="deployVersionModal = false" @ok="deployFromModal" ok-title="Deploy">
            <b-form-group :label="$t('deployTo')" label-for="deployEnv" :label-cols="3" :horizontal="true">
                <b-form-select v-model="deployEnv" :options="['dev', 'live']"></b-form-select>
            </b-form-group>
            <b-form-group :label="$t('version')" label-for="version" :label-cols="3" :horizontal="true">
                <b-form-input type="text" :value="currentVersion" disabled></b-form-input>
            </b-form-group>
        </main-dialog>

        <main-dialog
            :show="dependencyRemoval.show"
            :title="$t('removeDependencyTitle')"
            @close="dependencyRemoval.show = false">
            <b-alert show variant="warning">
                <strong>{{ $t('removeDependencyWarning', { dep: dependencyRemoval.target }) }}</strong>
            </b-alert>
            <p>{{ $t('removeDependencyDetail') }}</p>
            <template #footer>
                <b-button variant="secondary" @click="dependencyRemoval.show = false" class="mr-2">{{ $t('cancel') }}</b-button>
                <b-button variant="danger" @click="confirmRemoveDependency">{{ $t('iAmSureRemove') }}</b-button>
            </template>
        </main-dialog>
    </div>
    <div v-else class="cs-loading">
        <b-spinner></b-spinner>
    </div>
</template>

<script>
import { Switch as cSwitch } from '@coreui/vue'
import Fields from './Settings/FieldsV2'
import ListFields from './Settings/ListFields'
import Presets from './Settings/Presets'
import Appearance from './Settings/Appearance'
import Actions from './Settings/Actions'
import Events from './Settings/Events'
import Routes from './Settings/Routes'
import RenderEvent from '../integrations/RenderEvent'
import _ from 'lodash'
import Labeli18n from '../form/Texti18n.vue'
import VSelect from 'vue-select'
import ChooseIcon from '../elements/ChooseIcon'
import Functions from './Settings/Functions'
import CodeEditor from '../form/Editor/Code'
import Component from '@/lib/Component'
import UniqueIndexes from './Settings/UniqueIndexes'
import Extensions from './Settings/Extensions'
import Policies from './Settings/Policies'
import { idlTypeMap } from '@/components/identityFields/defs'

export default {
    components: {
        cSwitch,
        Fields,
        ListFields,
        Appearance,
        Actions,
        Events,
        Routes,
        RenderEvent,
        Presets,
        Labeli18n,
        VSelect,
        ChooseIcon,
        Functions,
        CodeEditor,
        UniqueIndexes,
        Extensions,
        Policies,
    },
    props: {
        id: {
            type: String,
            default: null
        }
    },
    data () {
        return {
            model: null,
            modelCopy: null,
            ready: false,
            saving: false,
            tabIndex: 0,
            tabHash: ["#general", "#appearance", "#list", "#fields", "#model", "#workflow", "#functions", "#config", "#extensions", "#idl"],
            forkVersionModal: false,
            deployVersionModal: false,
            deployEnv: 'dev',
            currentVersion: this.componentDefaultVersion,
            deploy: {
                env: '',
                version: ''
            },
            fork: {
                from: '',
                to: ''
            },
            forkSchemaModal: false,
            forkSchema: {
                data: '',
                version: ''
            },
            clone: {
                modal: false,
                name: ''
            },
            forceSettingsChanged: false,
            spaces: [],
            selectedDevVersion: null,
            selectedLiveVersion: null,
            selectedWorkflowSource: 'component',
            extensions: [],
            policies: [],
            dependencyRemoval: {
                show: false,
                target: null
            }
        }
    },
    computed: {
        ref() {
            const raw = this.id || this.$route.params.ref
            if (raw && raw.includes('/')) {
                return raw.replace('/', '::')
            }
            return raw
        },

        currentSchema() {
            const ref = this.ref
            if (!ref) return 'default'
            const base = ref.split('/')[0]
            const dotIdx = base.lastIndexOf('.')
            return dotIdx >= 0 ? base.substring(dotIdx + 1) : 'default'
        },

        currentSchemaIDL() {
            const idl = this.model && this.model.settings && this.model.settings.idl
            if (!idl || typeof idl !== 'object') return null
            if (idl.schemas && typeof idl.schemas === 'object') {
                return idl.schemas[this.currentSchema] || null
            }
            return idl[this.currentSchema] || null
        },

        idlJson: {
            get() {
                return JSON.stringify(this.model && this.model.settings && this.model.settings.idl ? this.model.settings.idl : {}, null, 2)
            },
            set(v) {
                try {
                    this.$set(this.model.settings, 'idl', JSON.parse(v))
                } catch (e) { /* ignore invalid JSON */ }
            }
        },

        idlFieldWarnings() {
            const idl = this.model && this.model.settings && this.model.settings.idl
            if (!idl || typeof idl !== 'object') return []
            const fieldsByKey = {}
            ;(this.model.settings.fields || []).forEach(f => { fieldsByKey[f.key] = f })
            const warnings = []

            const schemasMap = (idl.schemas && typeof idl.schemas === 'object') ? idl.schemas : idl
            for (const [schema, schemaIDL] of Object.entries(schemasMap)) {
                if (!schemaIDL || !schemaIDL.fields) continue
                Object.entries(schemaIDL.fields).forEach(([key, typeNode]) => {
                    const idlType = typeNode && typeNode.type
                    if (!idlType || idlType === 'any') return
                    const field = fieldsByKey[key]
                    if (!field) return
                    if (idlType === 'relation') return
                    const widgetType = idlTypeMap[`${field.module}:${field.type}`]
                    if (!widgetType || widgetType === 'any') return
                    if (widgetType !== idlType) {
                        warnings.push({ key, widgetType, idlType, module: field.module, type: field.type, schema })
                    }
                })
            }
            return warnings
        },

        fields() {
            return this.model.settings.fields || []
        },

        settingsChanged() {
            if (this.forceSettingsChanged) return true;

            if (!this.model || !this.modelCopy) {
                return false
            }

            const modelForComparison = _.cloneDeep(this.model)
            const modelCopyForComparison = _.cloneDeep(this.modelCopy)

            if (modelForComparison.settings) {
                delete modelForComparison.settings.extensions
                delete modelForComparison.settings.policies
            }
            if (modelCopyForComparison.settings) {
                delete modelCopyForComparison.settings.extensions
                delete modelCopyForComparison.settings.policies
            }

            return !_.isEqual(modelCopyForComparison, modelForComparison)
        },

        canBeActive() {
            return this.model.settings.fields !== null || this.fields.length > 0
        },

        canBePublic() {
            return this.canBeActive && this.model.general.is_active
        },

        canRemove() {
            if (this.model.general.dependencies === null) return true

            return this.model.general.dependencies.length == 1 && this.model.general.dependencies[0] == "platform"
        },

        componentConfig: {
            get() {
                return JSON.stringify(this.model.settings.config || {}, null, 4)
            },

            set(val) {
                this.model.settings.config = JSON.parse(val)
            }
        },

        modelCleanupAfter: {
            get() {
                return this.model.settings.model.cleanup_after
            },

            set() {
                this.model.settings.model.cleanup_after = null
            }
        },

        componentVersions: {
            get() {
                if (this.model === null) {
                    return []
                }

                if (this.model.general === undefined || this.model.general === null) {
                    return []
                }

                if (!this.model.general.versions) {
                    return []
                }

                return this.model.general.versions.map(v => {
                    return {
                        text: v.version,
                        value: v.version
                    }
                }).reverse()
            },

            set(val) {
                this.model.general.versions = val
            }
        },

        workspaces() {
            return this.$store.getters.getCurrentProject.workspaces.map(w => {
                return {
                    text: w.name,
                    value: w.uuid
                }
            })
        },

        workflowSources() {
            const sources = [
                {
                    label: this.$t('componentWorkflow'),
                    value: 'component'
                }
            ]

            if (this.model && this.model.settings && this.model.settings.extensions) {
                this.model.settings.extensions.forEach(extension => {
                    if (extension.is_active) {
                        sources.push({
                            label: `${this.$t('extension')}: ${extension.name}`,
                            value: `extension:${extension.name}`
                        })
                    }
                })
            }

            return sources
        },

        selectedExtension() {
            if (this.selectedWorkflowSource && this.selectedWorkflowSource.startsWith('extension:')) {
                return this.selectedWorkflowSource.replace('extension:', '')
            }
            return null
        },

        currentWorkflowEvents: {
            get() {
                if (this.selectedExtension) {
                    const extension = this.model.settings.extensions.find(
                        ext => ext.name === this.selectedExtension
                    )
                    if (extension) {
                        if (!extension.workflows) {
                            this.$set(extension, 'workflows', [])
                        }
                        return extension.workflows
                    }
                    return []
                }
                return this.model.settings.model.events || []
            },
            set(value) {
                if (this.selectedExtension) {
                    const extension = this.model.settings.extensions.find(
                        ext => ext.name === this.selectedExtension
                    )
                    if (extension) {
                        this.$set(extension, 'workflows', value)
                    }
                } else {
                    this.$set(this.model.settings.model, 'events', value)
                }
            }
        },

        componentDefaultVersion() {
            if (this.$isPlatformEnv('dev')) {
                return this.model.general.dev_version
            }

            if (this.$isPlatformEnv('live')) {
                return this.model.general.public_version || this.model.general.dev_version
            }

            return null
        },

        activeExtensions() {
            if (!this.model || !this.model.settings || !this.model.settings.extensions) {
                return []
            }
            return this.model.settings.extensions.filter(ext => ext.is_active)
        },

        listLink() {
            return this.$route.path.replace("/settings", "")
        }
    },
    watch: {
        model: {
            handler() {
                if (!this.model) return;

                let { tag, scope } = this.model.general

                if (scope) {
                    this.model.general.scope = scope.replaceAll(" ", "_").replaceAll("::","_").replaceAll(/[^a-zA-Z0-9_-]/g, '')
                }

                this.model.general.tag = tag.replaceAll(" ", "_").replaceAll(/[^a-zA-Z0-9_-]/g, '')

            },
            deep: true
        },

        watch:{
            $route (){
                 let m = this.model
                if (m === undefined) {
                    this.$store.dispatch("fetchComponentsSettings", this.ref).then((data) => {

                        this.model = _.cloneDeep(data)
                        this.modelCopy = _.cloneDeep(this.model)

                        this.extensions = data.settings.extensions || []
                        this.policies = data.settings.policies || []

                        this.$store.commit("SET_CURRENT_COMPONENT", data)
                        this.$store.commit("SET_COMPONENTS_SETTINGS", data)
                    })

                    return
                }

                this.model = _.cloneDeep(m)
                this.modelCopy = _.cloneDeep(this.model)

                this.$store.commit("SET_CURRENT_COMPONENT", m)

                this.currentVersion = this.componentDefaultVersion;

                let hash = window.location.hash
                this.tabIndex = this.tabHash.indexOf(hash)
            }
        }
    },

    methods: {
        handleKeydown(e) {
            if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                e.preventDefault()
                if (this.settingsChanged && !this.saving) {
                    this.saveSettings()
                }
            }
        },

        deployFromModal() {
            this.deployVersion(this.deployEnv, this.currentVersion)
        },

        updateExtensions(extensions) {
            this.extensions = extensions
            this.$set(this.model.settings, 'extensions', extensions)
        },

        updatePolicies(policies) {
            this.policies = policies
            this.$set(this.model.settings, 'policies', policies)
        },

        getExtensionConfig(extension) {
            return JSON.stringify(extension.config || {}, null, 4)
        },

        getExtensionIdl(extension) {
            return JSON.stringify(extension.idl || {}, null, 4)
        },

        generateIdlFromFields() {
            const fields = this.model.settings.fields || []
            if (!fields.length) {
                this.$bvToast.toast(this.$t('idl.noFields'), {
                    title: this.$t('idl.autoGenerate'),
                    variant: 'warning'
                })
                return
            }

            const schemaMap = {}
            fields.forEach(f => {
                const schema = f.schema || 'default'
                if (!schemaMap[schema]) schemaMap[schema] = {}

                if (f.module === 'select' && f.type === 'relation' && f.relation) {
                    const cardinality = f.relation.type === 'one-to-many' ? 'many' : 'one'
                    schemaMap[schema][f.key] = {
                        type: 'relation',
                        cardinality,
                        required: !!(f.constraints && f.constraints.required)
                    }
                    return
                }

                const idlType = idlTypeMap[`${f.module}:${f.type}`] || 'any'
                schemaMap[schema][f.key] = {
                    type: idlType,
                    required: !!(f.constraints && f.constraints.required)
                }
            })

            const existing = (this.model.settings && this.model.settings.idl) || {}
            const idl = {
                ...existing,
                schemas: {}
            }

            if (existing.types) idl.types = existing.types
            if (existing.functions) idl.functions = existing.functions

            for (const [schema, fieldsObj] of Object.entries(schemaMap)) {
                const existingSchema = (existing.schemas && existing.schemas[schema]) || {}
                idl.schemas[schema] = {
                    ...existingSchema,
                    fields: fieldsObj
                }
            }

            if (existing.schemas) {
                for (const [schema, schemaVal] of Object.entries(existing.schemas)) {
                    if (!idl.schemas[schema]) {
                        idl.schemas[schema] = schemaVal
                    }
                }
            }

            this.$set(this.model.settings, 'idl', idl)

            this.$bvToast.toast(this.$t('idl.autoGenerateSuccess', { count: fields.length }), {
                title: this.$t('idl.autoGenerate'),
                variant: 'success'
            })
        },

        saveSettings() {
            this.saving = true;
            let uuid = this.ref

            let version = this.currentVersion
            if (version === undefined) {
                version = this.componentDefaultVersion
            }

            this.$http.post("/v4/system/component/settings/" + uuid + "/" + version, this.model).then((resp) => {
                if (resp == null) {
                    throw new Error("unexpected error")
                }

                this.$bvToast.toast(this.$t('success'), {
                    variant: 'success',
                })

                this.modelCopy = _.cloneDeep(this.model)
                this.model = new Component(resp.data)

                this.extensions = this.model.settings.extensions || []
                this.policies = this.model.settings.policies || []

                let ref = this.model.general.name
                if (this.model.general.scope) {
                    ref = this.model.general.scope + "/" + ref
                }

                this.$router.push(`/admin/component/${ref}/settings`)

                this.$store.commit("SET_COMPONENTS_SETTINGS", this.model)
                this.$store.commit("UPDATE_COMPONENTS_MENU_ICON", {uuid: uuid, icon: this.model.settings.appearance.icon})
                this.$store.commit("UPDATE_COMPONENTS_MENU_ACTIVE", {uuid: uuid, is_active: this.model.general.is_active})
                this.$store.commit("UPDATE_COMPONENTS_MENU_SORT", {uuid: uuid, sort: this.model.general.sort})
            }).catch(error => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }

                    this.$mainDialog.msgBoxOk(error.response.data.message)
                    return
                }

                this.$mainDialog.msgBoxOk(error.message)
            }).finally(() => {
                this.saving = false
            })
        },

        copySchema() {
            navigator.clipboard.writeText(JSON.stringify(this.model.settings));
        },

        restoreSettings() {
            this.model = _.cloneDeep(this.modelCopy)
            this.extensions = this.modelCopy.settings.extensions || []
            this.policies = this.modelCopy.settings.policies || []
        },

        tabChange(e) {
            window.location.hash = e
        },

        removeComponent() {
            let uuid = this.ref

            this.$mainDialog.msgBoxConfirm(this.$t('thisActionRemove')).then(resp => {
                if (resp) {
                    this.$http.delete("/v3/system/component/" + uuid).then(() => {
                        this.$store.commit("REMOVE_COMPONENT", uuid)
                        this.$router.push({path: "/admin/dashboard"})
                    }).catch(err => {
                        if (err.response.status == 500) {
                            this.$serverErrorModal(err)
                            return
                        }

                        this.$mainDialog.msgBoxOk(err.response.data.message)
                    })
                }
            })
        },

        cloneComponent() {
            this.$http.post("/v3/system/component/clone", {name: this.clone.name, ref: this.ref}).then((resp) => {
                this.$router.push({path: "/admin/component/" + resp.data.uuid + "/settings"})
            }).catch(err => {
                if (err.response.status == 500) {
                    this.$serverErrorModal(err)
                    return
                }

                this.$mainDialog.msgBoxOk(err.response.data.message)
            })
        },

        onDependencyChange(newDeps) {
            var oldDeps = this.model.general.dependencies || []
            var removed = oldDeps.filter(function(d) { return newDeps.indexOf(d) === -1 })
            if (removed.length === 0) return

            var removedDep = removed[0]
            if (removedDep === 'platform') {
                return
            }

            this.dependencyRemoval.target = removedDep
            this.dependencyRemoval.show = true
        },

        confirmRemoveDependency() {
            var target = this.dependencyRemoval.target
            var deps = (this.model.general.dependencies || []).filter(function(d) { return d !== target })
            var nonPlatform = deps.filter(function(d) { return d !== 'platform' })

            if (nonPlatform.length === 0 && deps.indexOf('platform') === -1) {
                deps.push('platform')
            }

            this.$set(this.model.general, 'dependencies', deps)
            this.dependencyRemoval.show = false
            this.dependencyRemoval.target = null

            if (nonPlatform.length === 0) {
                this.saveSettings()
            }
        },

        previewVersion() {

            let uuid = this.ref
            this.model = null
            this.$http.get("/v3/system/component/settings/" + uuid + "/" + this.currentVersion).then((resp) => {
                this.model = resp.data
                this.model.general = new Component(this.model.general)
                this.modelCopy = _.cloneDeep(this.model)

                this.extensions = resp.data.settings.extensions || []
                this.policies = resp.data.settings.policies || []
            }).catch(error => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }
                }

                this.$mainDialog.msgBoxOk(error.response.data.message)
            })
        },

        initFork() {
            this.forkVersionModal = true;
            this.fork.from = this.currentVersion;
        },

        forkSchemaAction() {
            this.$http.post("/v3/system/component/version/fork/schema", {
                schema: this.forkSchema.data,
                version: this.forkSchema.version,
                ref: this.ref
            }).then(() => {
                this.currentVersion = this.forkSchema.version
                this.previewVersion()
                this.componentVersions.push({
                    text: this.forkSchema.version,
                    value: this.forkSchema.version
                })
            }).catch(error => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }
                }

                this.$mainDialog.msgBoxOk(error.response.data.message)
            })
        },

        forkVersion() {
            let found = this.componentVersions.find(v => v.value == this.fork.to)
            if (found) {
                this.$bvToast.toast(this.$t('cantForkVersionExist'), {
                    title: this.$t('error'),
                    variant: 'danger'
                })

                return
            }

            this.$http.post("/v3/system/component/version/fork", {
                name: this.fork.to,
                from: this.fork.from,
                ref: this.ref
            }).then(() => {
                this.currentVersion = this.fork.to
                this.previewVersion()
                this.componentVersions.push({
                    text: this.currentVersion,
                    value: this.currentVersion
                })
            }).catch(error => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }
                }

                this.$mainDialog.msgBoxOk(error.response.data.message)
            })
        },

        removeCurrentVersion() {
            this.$http.post("/v3/system/component/version/remove", {
                version: this.currentVersion,
                ref: this.ref
            }).then(() => {
                this.componentVersions = this.componentVersions.filter(v => {
                    return v.value !== this.currentVersion
                })

                this.currentVersion = this.componentVersions[0].value
            }).catch(error => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }
                }

                this.$mainDialog.msgBoxOk(error.response.data.message)
            })
        },

        deployVersion(env, version) {
            if (!version) return;

            const envLabel = env === 'dev' ? this.$t('dev') : this.$t('live');
            const currentVersion = env === 'dev' ? this.model.general.dev_version : this.model.general.public_version;

            let confirmMessage = this.$t('confirmDeployVersion', { version: version, env: envLabel });
            if (currentVersion) {
                confirmMessage += ' ' + this.$t('currentVersionWillBeReplaced', { currentVersion: currentVersion });
            }

            this.$mainDialog.msgBoxConfirm(confirmMessage, {
                title: this.$t('confirmDeployment'),
                okTitle: this.$t('deploy'),
                okVariant: env === 'dev' ? 'success' : 'primary',
                cancelTitle: this.$t('cancel')
            }).then(confirmed => {
                if (!confirmed) return;

                var popup = this.$loadingPopup(this.$t('deployingVersion') + " " + version + " " + this.$t('to') + " " + envLabel);

                this.$http.post("/v3/system/component/version/publish", {
                    version: version,
                    env: env,
                    ref: this.ref
                }).then(() => {
                    if (env == 'dev') {
                        this.model.general.dev_version = version;
                        this.selectedDevVersion = version;

                        this.modelCopy = _.cloneDeep(this.model)

                        this.$store.commit("SET_COMPONENTS_SETTINGS", this.model)
                        this.$store.commit("UPDATE_COMPONENTS_MENU_ICON", {uuid: this.model.general.uuid, icon: this.model.settings.appearance.icon})
                        this.$store.commit("UPDATE_COMPONENTS_MENU_ACTIVE", {uuid: this.model.general.uuid, is_active: this.model.general.is_active})
                        this.$store.commit("UPDATE_COMPONENTS_MENU_SORT", {uuid: this.model.general.uuid, sort: this.model.general.sort})
                    }

                    if (env == 'live') {
                       this.model.general.public_version = version;
                       this.selectedLiveVersion = version;
                       this.modelCopy = _.cloneDeep(this.model)
                    }

                    this.$bvToast.toast(this.$t('versionDeployedSuccessfully', { version: version, env: envLabel }), {
                        title: this.$t('success'),
                        variant: 'success'
                    })

                }).catch(error => {
                    if (error.response !== undefined) {
                        if (error.response.status == 500) {
                            this.$serverErrorModal(error)
                            return
                        }

                        this.$mainDialog.msgBoxOk(error.response.data.message)
                    }

                }).finally(() => {
                    popup.hide()
                })
            })
        },

        deployToLive(version) {
            this.$mainDialog.msgBoxConfirm(this.$t('areYouSureDeployVersion') + ' ' + this.currentVersion + ' ' + this.$t('asLive') + ' ' + this.model.general.public_version + ".", {
                okTitle: "Proceed",
                okVariant: "success"
            }).then(resp => {
                if (resp) {
                    let popup = this.$loadingPopup(this.$t('deployVersion') + ' ' + version + ' ' + this.$t('toLiveEnv'))
                    this.$http.post("/v3/system/component/version/publish", {
                        version: version,
                        env: 'live',
                        ref: this.ref
                    }).then(() => {
                        this.$mainDialog.msgBoxOk(this.$t('version') + ' ' + version + ' ' + this.$t('deployedAsLive'))
                        this.model.general.public_version = version
                        this.modelCopy = _.cloneDeep(this.model)
                    }).catch(error => {
                        if (error.response !== undefined) {
                            if (error.response.status == 500) {
                                this.$serverErrorModal(error)
                                return
                            }
                        }

                        this.$mainDialog.msgBoxOk(error.response.data.message)
                    }).finally(() => {
                        popup.hide()
                    })
                }
            })

        },

         deployToDev(version) {
            this.$http.post("/v3/system/component/version/publish", {
                version: version,
                env: 'dev',
                ref: this.ref
            }).then(() => {
                this.version = this.form.to
                this.previewVersion()
            }).catch(error => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }
                }

                this.$mainDialog.msgBoxOk(error.response.data.message)
            })
        },

        canBeUpdated() {
            return this.currentVersion !== this.model.general.public_version
        },

        workspaceChange() {
            this.forceSettingsChanged = true;
        }

    },
    beforeRouteLeave(to, from, next) {
        if (this.settingsChanged) {
            this.$mainDialog.msgBoxConfirm(this.$t('unsavedSettings')).then(resp => {
                if (resp) {
                    if (!this.saveSettings()) {
                        return
                    }
                } else {
                    this.restoreSettings()
                }

                next()
            })
        } else {
            next()
        }
    },

    created() {
        document.addEventListener('keydown', this.handleKeydown)
    },

    beforeDestroy() {
        document.removeEventListener('keydown', this.handleKeydown)
    },

    mounted() {
        this.$store.dispatch("fetchComponentsSettings", this.ref).then((data) => {
            this.model = new Component(_.cloneDeep(data))
            this.modelCopy = _.cloneDeep(this.model)

            this.extensions = data.settings.extensions || []
            this.policies = data.settings.policies || []

            this.$store.commit("SET_CURRENT_COMPONENT", data)
            this.$store.commit("SET_COMPONENTS_SETTINGS", data)

            this.currentVersion = this.componentDefaultVersion
            this.selectedDevVersion = this.model.general.dev_version
            this.selectedLiveVersion = this.model.general.public_version

            let hash = window.location.hash
            this.tabIndex = this.tabHash.indexOf(hash)
        })

    }
}
</script>

<style scoped>
/* ── Page ───────────────────────────────────────── */
.cs-page {
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 120px);
}

.cs-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 120px;
}

/* ── Header ─────────────────────────────────────── */
.cs-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0 16px;
    border-bottom: 1px solid var(--border-default);
    gap: 16px;
    flex-wrap: wrap;
}

.cs-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
}

.cs-back {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    color: var(--text-muted);
    transition: all 0.15s;
    flex-shrink: 0;
}

.cs-back:hover {
    background: var(--bg-card-secondary);
    color: var(--text-primary);
    text-decoration: none;
}

.cs-identity {
    min-width: 0;
}

.cs-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.cs-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    flex-shrink: 0;
}

.cs-dot--active {
    background: var(--success-start, #48bb78);
    box-shadow: 0 0 0 3px rgba(72, 187, 120, 0.2);
}

.cs-dot--inactive {
    background: var(--text-disabled, #6d7480);
}

.cs-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.cs-unsaved {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--warning-start, #ed8936);
    flex-shrink: 0;
    animation: cs-pulse 2s ease-in-out infinite;
}

@keyframes cs-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
}

.cs-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 2px;
}

.cs-scope-chip {
    display: inline-flex;
    align-items: center;
    padding: 1px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    background: rgba(108, 128, 255, 0.10);
    color: var(--primary-start, #6c80ff);
    border: 1px solid rgba(108, 128, 255, 0.2);
}

.cs-ref {
    font-size: 12px;
    color: var(--text-muted);
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
}

.cs-header-actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

/* ── Version Pipeline ──────────────────────────── */
.cs-versions {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 16px;
    margin: 12px 0;
    background: var(--bg-card, #313338);
    border: 1px solid var(--border-default);
    border-radius: 8px;
    flex-wrap: wrap;
}

.cs-version-slot {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.cs-version-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: var(--text-muted, #949ba4);
}

.cs-version-select {
    max-width: 160px;
}

.cs-version-info {
    display: flex;
    align-items: center;
    gap: 8px;
}

.cs-version-chip {
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
    white-space: nowrap;
}

.cs-version-chip--dev {
    background: rgba(72, 187, 120, 0.10);
    color: var(--success-start, #48bb78);
    border: 1px solid rgba(72, 187, 120, 0.2);
}

.cs-version-chip--live {
    background: rgba(108, 128, 255, 0.10);
    color: var(--primary-start, #6c80ff);
    border: 1px solid rgba(108, 128, 255, 0.2);
}

.cs-version-none {
    font-size: 13px;
}

.cs-version-arrow {
    color: var(--text-disabled, #6d7480);
    flex-shrink: 0;
    font-size: 14px;
}

.cs-deploy-btn {
    padding: 3px 8px !important;
    line-height: 1;
}

/* ── Tabs ──────────────────────────────────────── */
.cs-tabs ::v-deep .nav {
    border-bottom: 1px solid var(--border-default);
    gap: 0;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
}

.cs-tabs ::v-deep .nav::-webkit-scrollbar {
    display: none;
}

.cs-tabs ::v-deep .nav-item .nav-link {
    border-radius: 0;
    border: none;
    border-bottom: 2px solid transparent;
    background: none !important;
    color: var(--text-muted, #949ba4);
    padding: 10px 16px;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    transition: all 0.15s;
}

.cs-tabs ::v-deep .nav-item .nav-link:hover {
    color: var(--text-secondary, #b5bac1);
    border-bottom-color: var(--border-hover);
}

.cs-tabs ::v-deep .nav-item .nav-link.active {
    color: var(--primary-start, #6c80ff) !important;
    border-bottom-color: var(--primary-start, #6c80ff) !important;
    background: none !important;
}

.cs-tab-content {
    padding-top: 20px;
}

/* ── Cards ─────────────────────────────────────── */
.cs-card {
    background: var(--bg-card, #313338);
    border: 1px solid var(--border-default);
    border-radius: 10px;
    padding: 20px 24px;
    margin-bottom: 16px;
}

.cs-card-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 18px;
    display: flex;
    align-items: center;
}

.cs-card--danger {
    border-color: rgba(245, 101, 101, 0.25);
    background: rgba(245, 101, 101, 0.03);
}

.cs-card--danger .cs-card-title {
    color: var(--danger-start, #f56565);
}

/* ── Form Grid ─────────────────────────────────── */
.cs-form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
}

.cs-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.cs-field--full {
    grid-column: 1 / -1;
}

.cs-field > label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted, #949ba4);
    margin-bottom: 0;
}

.cs-field > small {
    font-size: 11px;
}

.cs-readonly-value {
    font-size: 13px;
    color: var(--text-secondary, #b5bac1);
    padding: 6px 0;
}

/* ── Toggles ───────────────────────────────────── */
.cs-toggles {
    margin-top: 18px;
    border-top: 1px solid var(--border-default);
    padding-top: 16px;
}

.cs-toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
}

.cs-toggle-row + .cs-toggle-row {
    border-top: 1px solid var(--border-separator, rgba(255, 255, 255, 0.06));
}

.cs-toggle-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
    display: block;
}

.cs-toggle-desc {
    font-size: 12px;
    color: var(--text-muted);
    display: block;
    margin-top: 1px;
}

/* ── Dependencies ──────────────────────────────── */
.cs-dependencies {
    margin-top: 18px;
    border-top: 1px solid var(--border-default);
    padding-top: 16px;
}

.cs-dependencies > label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted, #949ba4);
    margin-bottom: 6px;
    display: block;
}

.cs-dependencies > small {
    font-size: 11px;
    display: block;
    margin-top: 4px;
}

/* ── Action Grid ───────────────────────────────── */
.cs-action-grid {
    display: flex;
    flex-direction: column;
    gap: 0;
}

.cs-action-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
}

.cs-action-item + .cs-action-item {
    border-top: 1px solid var(--border-separator, rgba(255, 255, 255, 0.06));
}

.cs-card--danger .cs-action-item + .cs-action-item {
    border-top-color: rgba(245, 101, 101, 0.12);
}

.cs-action-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
    display: block;
}

.cs-action-desc {
    font-size: 12px;
    color: var(--text-muted);
    display: block;
    margin-top: 1px;
}

/* ── Extensions Badge ──────────────────────────── */
.cs-ext-header {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px 0;
    margin-bottom: 12px;
    font-size: 13px;
    color: var(--text-secondary);
    border-bottom: 1px solid var(--border-default);
}

.cs-ext-badge {
    display: inline-flex;
    align-items: center;
    padding: 1px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    background: rgba(72, 187, 120, 0.10);
    color: var(--success-start, #48bb78);
    border: 1px solid rgba(72, 187, 120, 0.2);
    margin-left: 8px;
}

/* ── Section Title ─────────────────────────────── */
.cs-section-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 24px 0 12px;
}

/* ── IDL ───────────────────────────────────────── */
.cs-idl-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
    flex-wrap: wrap;
}

.cs-idl-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

.cs-doc-link {
    color: var(--text-muted);
    text-decoration: none;
    white-space: nowrap;
}

.cs-doc-link:hover {
    color: var(--primary-start);
}

/* ── V-Select Overrides ────────────────────────── */
::v-deep .v-select .vs__dropdown-toggle {
    min-height: 34px;
}

::v-deep .v-select .vs__selected-options {
    min-height: 32px;
}

::v-deep .v-select input[type="search"] {
    margin: 0;
}

/* ── Responsive ────────────────────────────────── */
@media (max-width: 768px) {
    .cs-form-grid {
        grid-template-columns: 1fr;
    }

    .cs-versions {
        flex-direction: column;
        align-items: flex-start;
    }

    .cs-version-arrow {
        transform: rotate(90deg);
    }

    .cs-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .cs-header-actions {
        width: 100%;
        justify-content: flex-end;
    }
}
</style>
